import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
} from '@mui/material';
import {
  TransportationAttribute,
  TransportationAttributeGroup,
  TransportationAttributeSubgrouping,
} from '../../../data/models/transportation-attribute.model';
import { TransportationAttributeCategory } from '../../../data/models/transportation-attribute-category.model';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import TextInput from '../../forms/TextInput';
import SelectMenu from '../../forms/SelectMenu';
import Checkbox from '../../forms/Checkbox';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useLookups } from '../../../hooks/useLookups';
import * as uiSlice from '../../../data/ui.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import { useEffect, useMemo } from 'react';

const PREFIX = 'TransportationAttributeDialog';

const classes = {
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
  actionButtons: `${PREFIX}-actionButtons`,
  actionButton: `${PREFIX}-actionButton`,
  checkbox: `${PREFIX}-checkbox`,
  defaultCheckbox: `${PREFIX}-defaultCheckbox`,
  displayOrder: `${PREFIX}-displayOrder`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '600px',
    maxWidth: '600px',
  },
  '& .MuiDialogTitle-root': {
    ...theme.typography.h3,
    paddingBottom: theme.spacing(1),
  },
  [`& .${classes.content}`]: {
    paddingTop: theme.spacing(2),
    gap: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'end',
  },
  [`& .${classes.actionButtons}`]: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  [`& .${classes.actionButton}`]: {
    width: '178px',
    height: '48px',
  },
  [`& .${classes.checkbox}`]: {
    marginLeft: theme.spacing(0.5),
    '& .MuiFormControlLabel-label': {
      ...theme.typography.body1,
    },
  },
  [`& .${classes.defaultCheckbox}`]: {
    marginBottom: theme.spacing(-1),
  },
  [`& .${classes.displayOrder}`]: {
    width: '190px',
  },
}));

export enum TransportationAttributeDialogAction {
  Add = 'add',
  Edit = 'edit',
  Duplicate = 'duplicate',
  View = 'view',
}

interface TransportationAttributeDialogProps {
  action?: TransportationAttributeDialogAction;
  transAttrb?: TransportationAttribute;
  categories: TransportationAttributeCategory[];
  topLevel: boolean;
  open: boolean;
  close: () => void;
}

export default function TransportationAttributeDialog({
  action,
  transAttrb,
  categories,
  topLevel,
  open,
  close,
}: TransportationAttributeDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  useLookups(['passengerCareLevel', 'tripMode']);
  const saving = useAppSelector(
    accountingSlice.selectSavingTransportationAttribute,
  );
  const saved = useAppSelector(
    accountingSlice.selectSavedTransportationAttribute,
  );
  const passengerCareLevels = useAppSelector(
    lookupSlice.selectPassengerCareLevels,
  );
  const tripModes = useAppSelector(lookupSlice.selectTripModes);
  const isReadOnly = action === TransportationAttributeDialogAction.View;

  const groups = [
    {
      groupingValue: '',
      groupingName: t('transportationAttributes.noGroup'),
    },
    {
      groupingValue: TransportationAttributeGroup.PRIMARY,
      groupingName: t(
        `transportationAttributes.${TransportationAttributeGroup.PRIMARY}`,
      ),
    },
    {
      groupingValue: TransportationAttributeGroup.SECONDARY,
      groupingName: t(
        `transportationAttributes.${TransportationAttributeGroup.SECONDARY}`,
      ),
    },
  ];

  const subgroups = [
    {
      subgroupingValue: '',
      subgroupingName: t('transportationAttributes.noSubgrouping'),
    },
    {
      subgroupingValue: TransportationAttributeSubgrouping.ADDITIONALPASSENGER,
      subgroupingName: t(
        `transportationAttributes.${TransportationAttributeSubgrouping.ADDITIONALPASSENGER}`,
      ),
    },
    {
      subgroupingValue: TransportationAttributeSubgrouping.OTHER,
      subgroupingName: t(
        `transportationAttributes.${TransportationAttributeSubgrouping.OTHER}`,
      ),
    },
  ];

  const noCategory: TransportationAttributeCategory = {
    transportationAttributeCategoryID: 0,
    transportationAttributeCategoryBC: '',
    transportationAttributeCategoryName: t('transportationAttributes.noChild'),
    isActive: true,
  };

  const noPassengerCareLevel = {
    passengerCareLevelID: 0,
    passengerCareLevelBC: '',
    passengerCareLevelName: t('transportationAttributes.noPassengerCareLevel'),
    isActive: true,
  };

  const noTripMode = {
    tripModeID: 0,
    tripModeBC: '',
    tripModeName: t('transportationAttributes.noTripMode'),
    isActive: true,
  };

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      description: '',
      transportationAttributeCategoryBC: '',
      grouping: '',
      subgrouping: '',
      childTransportationAttributeCategoryBC: '',
      isPassengerSelectable: false,
      isDefault: false,
      displayOrder: 0,
      passengerCareLevelBC: '',
      tripModeBC: '',
    },
  });

  const initialValues = useMemo(
    () => ({
      name: transAttrb?.name || '',
      description: transAttrb?.description || '',
      transportationAttributeCategoryBC:
        transAttrb?.transportationAttributeCategoryBC || '',
      grouping: transAttrb?.grouping || '',
      subgrouping: transAttrb?.subgrouping || '',
      displayOrder: transAttrb?.displayOrder || 0,
      isDefault: transAttrb?.isDefault || false,
      isPassengerSelectable: transAttrb?.isPassengerSelectable || false,
      childTransportationAttributeCategoryBC:
        transAttrb?.childTransportationAttributeCategoryBC || '',
      passengerCareLevelBC: transAttrb?.passengerCareLevelBC || '',
      tripModeBC: transAttrb?.tripModeBC || '',
    }),
    [transAttrb],
  );

  useEffect(() => {
    formHook.reset(initialValues);
  }, [initialValues]);

  const watchedValues = formHook.watch();

  const hasActualChanges = useMemo(() => {
    return (
      watchedValues.name !== initialValues.name ||
      watchedValues.description !== initialValues.description ||
      watchedValues.transportationAttributeCategoryBC !==
        initialValues.transportationAttributeCategoryBC ||
      watchedValues.grouping !== initialValues.grouping ||
      watchedValues.subgrouping !== initialValues.subgrouping ||
      Number(watchedValues.displayOrder) !==
        Number(initialValues.displayOrder) ||
      watchedValues.isDefault !== initialValues.isDefault ||
      watchedValues.isPassengerSelectable !==
        initialValues.isPassengerSelectable ||
      watchedValues.childTransportationAttributeCategoryBC !==
        initialValues.childTransportationAttributeCategoryBC ||
      watchedValues.passengerCareLevelBC !==
        initialValues.passengerCareLevelBC ||
      watchedValues.tripModeBC !== initialValues.tripModeBC
    );
  }, [watchedValues, initialValues]);

  useEffect(() => {
    if (saved && saveSuccessMessage) {
      handleClose();
    }
  }, [saved, saveSuccessMessage]);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleSave = (data: any) => {
    const displayOrder = parseInt(data.displayOrder) || 0;
    const attr: TransportationAttribute = {
      transportationAttributeUUID:
        transAttrb?.transportationAttributeUUID || undefined,
      transportationAttributeCategoryBC: data.transportationAttributeCategoryBC,
      name: data.name,
      description: data.description,
      isPassengerSelectable: data.isPassengerSelectable,
      isDefault: data.isDefault,
      childTransportationAttributeCategoryBC:
        data.childTransportationAttributeCategoryBC || undefined,
      grouping: topLevel ? data.grouping || undefined : undefined,
      subgrouping: data.subgrouping || undefined,
      displayOrder,
      passengerCareLevelBC: data.passengerCareLevelBC || undefined,
      tripModeBC: data.tripModeBC || undefined,
    };
    if (transAttrb) {
      dispatch(accountingSlice.updateTransportationAttributeAsync(attr));
    } else {
      dispatch(accountingSlice.createTransportationAttributeAsync(attr));
    }
  };

  const displayOrderMin = 0;
  const displayOrderMax = 999;

  return (
    <RootDialog open={open} onClose={close} maxWidth="md">
      <DialogTitle>
        {t(`transportationAttributes.${action}TransportationAttribute`)}
      </DialogTitle>

      <DialogContent>
        <Stack direction="column" className={classes.content}>
          <TextInput
            name="name"
            label={t('transportationAttributes.name')}
            control={formHook.control}
            required={true}
            errors={formHook.formState.errors}
            rules={{ required: requiredFieldMessage }}
            readOnly={isReadOnly}
          />
          <TextInput
            name="description"
            label={t('transportationAttributes.description')}
            control={formHook.control}
            required={true}
            errors={formHook.formState.errors}
            rules={{ required: requiredFieldMessage }}
            readOnly={isReadOnly}
          />
          <SelectMenu
            label={t('transportationAttributes.category')}
            name="transportationAttributeCategoryBC"
            values={categories}
            valueAttr="transportationAttributeCategoryBC"
            labelAttr="transportationAttributeCategoryName"
            required={true}
            control={formHook.control}
            readOnly={isReadOnly}
            errorMessage={
              formHook.formState.errors?.transportationAttributeCategoryBC
                ?.message
            }
            rules={{ required: requiredFieldMessage }}
          />
          {topLevel && (
            <SelectMenu
              label={t('transportationAttributes.grouping')}
              name="grouping"
              values={groups}
              valueAttr="groupingValue"
              labelAttr="groupingName"
              required={false}
              readOnly={isReadOnly}
              control={formHook.control}
            />
          )}

          <SelectMenu
            label={t('transportationAttributes.subgrouping')}
            name="subgrouping"
            values={subgroups}
            valueAttr="subgroupingValue"
            labelAttr="subgroupingName"
            required={false}
            readOnly={isReadOnly}
            control={formHook.control}
          />
          <TextInput
            label={t('transportationAttributes.displayOrder')}
            type="number"
            name="displayOrder"
            required={true}
            control={formHook.control}
            className={classes.displayOrder}
            errors={formHook.formState.errors}
            readOnly={isReadOnly}
            rules={{
              required: requiredFieldMessage,
              min: {
                value: displayOrderMin,
                message: t('transportationAttributes.displayOrderMin', {
                  min: displayOrderMin,
                }),
              },
              max: {
                value: displayOrderMax,
                message: t('transportationAttributes.displayOrderMax', {
                  max: displayOrderMax,
                }),
              },
            }}
          />
          <SelectMenu
            label={t('transportationAttributes.childCategory')}
            name="childTransportationAttributeCategoryBC"
            values={[noCategory, ...categories]}
            valueAttr="transportationAttributeCategoryBC"
            labelAttr="transportationAttributeCategoryName"
            required={false}
            readOnly={isReadOnly}
            control={formHook.control}
          />
          <SelectMenu
            label={t('transportationAttributes.passengerCareLevel')}
            name="passengerCareLevelBC"
            values={[noPassengerCareLevel, ...passengerCareLevels]}
            valueAttr="passengerCareLevelBC"
            labelAttr="passengerCareLevelName"
            required={false}
            readOnly={isReadOnly}
            control={formHook.control}
          />
          <SelectMenu
            label={t('transportationAttributes.tripMode')}
            name="tripModeBC"
            values={[noTripMode, ...tripModes]}
            valueAttr="tripModeBC"
            labelAttr="tripModeName"
            required={false}
            readOnly={isReadOnly}
            control={formHook.control}
          />
          <Checkbox
            name="isDefault"
            className={`${classes.checkbox} ${classes.defaultCheckbox}`}
            label={t('transportationAttributes.isDefault')}
            control={formHook.control}
            readOnly={isReadOnly}
          />
          <Checkbox
            name="isPassengerSelectable"
            className={classes.checkbox}
            label={t('transportationAttributes.isPassengerSelectable')}
            control={formHook.control}
            readOnly={isReadOnly}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Box className={classes.actionButtons}>
          {isReadOnly && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.actionButton}
              onClick={handleClose}>
              {t('common.close')}
            </Button>
          )}
          {!isReadOnly && (
            <>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                className={classes.actionButton}
                onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={
                  !formHook.formState.isValid || !hasActualChanges || saving
                }
                className={classes.actionButton}
                onClick={formHook.handleSubmit(handleSave)}>
                {transAttrb ? t('common.save') : t('common.add')}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </RootDialog>
  );
}
